import axios from "axios";
import { API_URL } from "@/config";
import downloadFile from "@/utils/downloadFile";
import { getMealsMenuWithFilteredInstitutions } from "./dtos";
import { useSubscribeForFile } from "@/utils/useSubscribeForFile";
import { resetStateToDefault } from "@/lib/store/index";
import { createTrackedEventSource } from "@/lib/eventSourceRegistry";

const defaultState = Object.freeze({
  updatePartsIsInProgress: false,
});

// Overall deadline for the async import to report back over Mercure. Split from the queue
// (worker also handles async_reports), so a busy report queue eating into this budget is
// expected - keep it generous rather than lowering it.
const IMPORT_TIMEOUT_MS = 15 * 60 * 1000;

// A dropped SSE connection is normal (idle stream, proxy hiccup) and the browser reconnects
// on its own - closing on the first error aborts the UI while the import keeps running server
// side. Only give up after several consecutive failed reconnect attempts, or immediately if
// the browser itself gives up (readyState CLOSED, e.g. an expired/invalid token).
const MAX_CONSECUTIVE_CONNECTION_ERRORS = 6;

const store = {
  namespaced: true,

  state: {
    ...defaultState,
  },

  mutations: {
    setUpdatePartsIsInProgress(state, value) {
      state.updatePartsIsInProgress = value;
    },

    resetState(state) {
      resetStateToDefault(state, defaultState);
    },
  },

  actions: {
    async getMany({ rootGetters }, criteria) {
      const response = await axios.get(`${API_URL}/mealsMenus`, { params: criteria });

      if (!response.data || typeof response.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      const currentUserInstitutionsIds = rootGetters["auth/currentUserInstitutionsIds"];

      return {
        ...response.data,
        items: response.data.items.map((item) =>
          getMealsMenuWithFilteredInstitutions(item, currentUserInstitutionsIds),
        ),
      };
    },

    async getOne(_, { mealsMenuId, criteria = {} }) {
      const response = await axios.get(`${API_URL}/mealsMenus/${mealsMenuId}`, {
        params: criteria,
      });

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async create(_, mealsMenuData) {
      const response = await axios.post(`${API_URL}/mealsMenus`, mealsMenuData);

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async updateParts({ commit }, { mealsMenuId, updateData }) {
      commit("setUpdatePartsIsInProgress", true);

      const response = await axios.put(
        `${API_URL}/mealsMenus/${mealsMenuId}/updateParts`,
        updateData,
      );

      if (response.status !== 200) {
        commit("setUpdatePartsIsInProgress", false);

        throw new Error("Error occurred while processing your request.");
      }

      commit("setUpdatePartsIsInProgress", false);

      return true;
    },

    async delete(_, mealsMenuId) {
      const response = await axios.delete(`${API_URL}/mealsMenus/${mealsMenuId}`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async importMealsMenuFromExcelFile({ commit }, formData) {
      const response = await axios.post(
        `${API_URL}/mealsMenus/importMealsMenuFromExcelFile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status !== 202) {
        throw new Error("Error occurred while processing your request.");
      }

      const { mercureInfo } = response.data;
      const linkHeader = response.headers["link"] || response.headers["x-link"];

      if (!linkHeader || !mercureInfo) {
        throw new Error("Missing Mercure subscription data.");
      }

      const mercureUrlMatch = linkHeader.match(/<([^>]+)>;\s*rel="mercure"/);
      if (!mercureUrlMatch) {
        throw new Error("No Mercure hub URL found.");
      }
      const mercureHubUrl = mercureUrlMatch[1];

      commit(
        "asyncImport/startImport",
        { type: "mealsMenu", label: formData.get("name") || "Nowy jadłospis" },
        { root: true },
      );

      return new Promise((resolve, reject) => {
        const url = new URL(mercureHubUrl);
        url.searchParams.append("topic", mercureInfo.mercureTopic);
        url.searchParams.append("authorization", mercureInfo.mercureToken);

        const eventSource = createTrackedEventSource(url.toString());

        const finish = (fn) => {
          clearTimeout(timeout);
          eventSource.close();
          commit("asyncImport/finishImport", undefined, { root: true });
          fn();
        };

        const timeout = setTimeout(
          () => {
            finish(() =>
              reject(new Error("Import is taking longer than expected. Please try again later.")),
            );
          },
          5 * 60 * 1000,
        );

        eventSource.onerror = () => {
          finish(() => reject(new Error("Connection error during import. Please try again.")));
        };

        eventSource.onmessage = (event) => {
          const message = JSON.parse(event.data);

          if (message.status === "done") {
            finish(() =>
              resolve({
                importedRowsCount: message.importedRowsCount,
                ...message.additionalInfo,
              }),
            );
          } else if (message.status === "error") {
            const err = new Error(message.message || "Import failed.");

            err.translationKey = message.translationKey || null;
            err.translationData = message.translationData || null;

            finish(() => reject(err));
          }
        };
      });
    },

    async importToExistingMealsMenu({ commit }, { mealsMenuId, mealsMenuName, formData }) {
      const response = await axios.post(
        `${API_URL}/mealsMenus/${mealsMenuId}/importFromExcel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status !== 202) {
        throw new Error("Error occurred while processing your request.");
      }

      const { mercureInfo } = response.data;
      const linkHeader = response.headers["link"] || response.headers["x-link"];

      if (!linkHeader || !mercureInfo) {
        throw new Error("Missing Mercure subscription data.");
      }

      const mercureUrlMatch = linkHeader.match(/<([^>]+)>;\s*rel="mercure"/);
      if (!mercureUrlMatch) {
        throw new Error("No Mercure hub URL found.");
      }
      const mercureHubUrl = mercureUrlMatch[1];

      commit(
        "asyncImport/startImport",
        { type: "mealsMenu", label: mealsMenuName },
        { root: true },
      );

      return new Promise((resolve, reject) => {
        const url = new URL(mercureHubUrl);
        url.searchParams.append("topic", mercureInfo.mercureTopic);
        url.searchParams.append("authorization", mercureInfo.mercureToken);

        const eventSource = createTrackedEventSource(url.toString());

        let consecutiveConnectionErrors = 0;

        const finish = (fn) => {
          clearTimeout(timeout);
          eventSource.close();
          commit("asyncImport/finishImport", undefined, { root: true });
          fn();
        };

        const timeout = setTimeout(
          () =>
            finish(() =>
              reject(new Error("Import is taking longer than expected. Please try again later.")),
            ),
          IMPORT_TIMEOUT_MS,
        );

        eventSource.onerror = () => {
          // The browser gave up reconnecting entirely - no point waiting further.
          if (eventSource.readyState === EventSource.CLOSED) {
            finish(() => reject(new Error("Connection error during import. Please try again.")));

            return;
          }

          consecutiveConnectionErrors += 1;

          if (consecutiveConnectionErrors > MAX_CONSECUTIVE_CONNECTION_ERRORS) {
            finish(() => reject(new Error("Connection error during import. Please try again.")));

            return;
          }

          // Otherwise the browser is already retrying the connection - let it.
        };

        eventSource.onmessage = (event) => {
          consecutiveConnectionErrors = 0;

          const message = JSON.parse(event.data);

          if (message.status === "done") {
            finish(() =>
              resolve({
                importedRowsCount: message.importedRowsCount,
                ...message.additionalInfo,
              }),
            );
          } else if (message.status === "error") {
            const err = new Error(message.message || "Import failed.");

            err.translationKey = message.translationKey || null;
            err.translationData = message.translationData || null;

            finish(() => reject(err));
          }
        };
      });
    },

    async getMealsMenusCount(_, { criteria }) {
      const response = await axios.get(`${API_URL}/mealsMenus/count`, { params: criteria });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getMealsMenusItems(_, criteria) {
      const response = await axios.get(`${API_URL}/mealsMenus/items`, { params: criteria });

      if (!response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getMealsMenuItemsData(_, { mealsMenuId, criteria }) {
      const response = await axios.get(`${API_URL}/mealsMenus/${mealsMenuId}/itemsData`, {
        params: criteria,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async createSimilar(_, { mealsMenuId, name }) {
      const response = await axios.post(`${API_URL}/mealsMenus/${mealsMenuId}/createSimilar`, {
        name,
      });

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOptions(_, criteria = {}) {
      const response = await axios.get(`${API_URL}/mealsMenus/options`, { params: criteria });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async print({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "mealsMenus/print",
        {
          url: `${API_URL}/mealsMenus/print`,
          method: "POST",
          data: criteria,
        },
        true,
      );
    },

    async findItem(_, { mealsMenuId, criteria = {} }) {
      const response = await axios.get(`${API_URL}/mealsMenus/${mealsMenuId}/items/find`, {
        params: criteria,
      });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getMealsMenuMonthData(_, { mealsMenuId, month }) {
      const response = await axios.get(`${API_URL}/mealsMenus/${mealsMenuId}/month/${month}/data`);

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async copyMealsMenuDayItemsToDates(_, { mealsMenuId, date, dates }) {
      const response = await axios.post(
        `${API_URL}/mealsMenus/${mealsMenuId}/items/dates/${date}/copy`,
        { dates },
      );

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async copyMealsMenuDaysItemsToDates(_, { mealsMenuId, datesToCopy, datesToPaste }) {
      const response = await axios.post(`${API_URL}/mealsMenus/${mealsMenuId}/items/dates/copy`, {
        datesToCopy,
        datesToPaste,
      });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async copyMealTypeToSelectedDates(_, { mealsMenuId, mealTypeId, mealTypeDate, selectedDates }) {
      const response = await axios.post(
        `${API_URL}/mealsMenus/${mealsMenuId}/items/dates/${mealTypeDate}/mealTypes/${mealTypeId}/copy`,
        {
          mealTypeId,
          mealTypeDate,
          selectedDates,
        },
      );

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async clearDay(_, { mealsMenuId, date }) {
      const response = await axios.post(`${API_URL}/mealsMenus/${mealsMenuId}/dates/${date}/clear`);

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async exportMealsMenusToDocx(_, payload) {
      // Jeśli payload zawiera klucz mealsMenuExportCriteria, wyciągnij jego zawartość
      const mealsMenuExportCriteria = payload.mealsMenuExportCriteria || payload;

      const response = await axios({
        url: `${API_URL}/mealsMenusExport/docx`,
        data: mealsMenuExportCriteria,
        method: "POST",
        responseType: "blob",
      });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      downloadFile(response);

      return true;
    },

    async exportMealsMenuByIdToDocx(_, { mealsMenuId, mealsMenuExportCriteria }) {
      const response = await axios({
        url: `${API_URL}/mealsMenusExport/docx/mealsMenu/${mealsMenuId}`,
        data: mealsMenuExportCriteria,
        method: "POST",
        responseType: "blob",
      });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      downloadFile(response);

      return true;
    },
  },
};

export default store;
